import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext.jsx';
import { api } from '../api/client.js';
import { clearHumanCycleCache } from '../utils/humanCycles.js';

const STORAGE_KEY = 'alinea_superadmin_tenant_id';

const TenantScopeContext = createContext(null);

export function TenantScopeProvider({ children }) {
    const { user } = useAuth();
    const isSuperAdmin = user?.rol === 'superadmin';
    const [tenants, setTenants] = useState([]);
    const [selectedTenantId, setSelectedTenantIdState] = useState(() => {
        if (typeof window === 'undefined') return '';
        return localStorage.getItem(STORAGE_KEY) || '';
    });

    const setSelectedTenantId = useCallback((id) => {
        setSelectedTenantIdState(id);
        if (id) localStorage.setItem(STORAGE_KEY, String(id));
        else localStorage.removeItem(STORAGE_KEY);
        clearHumanCycleCache();
    }, []);

    useEffect(() => {
        if (!isSuperAdmin) {
            setTenants([]);
            setSelectedTenantIdState('');
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await api.getSuperadminTenants();
                if (cancelled) return;
                const list = res?.data || [];
                setTenants(list);
                if (list.length) {
                    const saved = localStorage.getItem(STORAGE_KEY);
                    const validSaved = saved && list.some((t) => String(t.id) === saved);
                    setSelectedTenantId(validSaved ? saved : String(list[0].id));
                }
            } catch (e) {
                console.warn('Tenants fetch failed', e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isSuperAdmin, setSelectedTenantId]);

    const tenantId = isSuperAdmin && selectedTenantId ? selectedTenantId : null;
    const needsTenant = isSuperAdmin && !tenantId;

    const selectedTenantName = useMemo(() => {
        if (!tenantId) return '';
        const t = tenants.find((x) => String(x.id) === String(tenantId));
        return t?.name || '';
    }, [tenants, tenantId]);

    const value = useMemo(
        () => ({
            isSuperAdmin,
            tenants,
            selectedTenantId,
            setSelectedTenantId,
            tenantId,
            needsTenant,
            selectedTenantName,
        }),
        [isSuperAdmin, tenants, selectedTenantId, setSelectedTenantId, tenantId, needsTenant, selectedTenantName]
    );

    return <TenantScopeContext.Provider value={value}>{children}</TenantScopeContext.Provider>;
}

export function useTenantScope() {
    const ctx = useContext(TenantScopeContext);
    if (!ctx) {
        return {
            isSuperAdmin: false,
            tenants: [],
            selectedTenantId: '',
            setSelectedTenantId: () => {},
            tenantId: null,
            needsTenant: false,
            selectedTenantName: '',
        };
    }
    return ctx;
}
